import React from "react";
import {
  Card,
  Elevation,
  H3,
  H4,
  Button,
  Tag,
  Text,
  Divider,
  NonIdealState,
  Spinner,
  Dialog,
  Classes as DialogClasses,
  FileInput,
  Intent,
  ProgressBar,
  Callout,
  Icon,
  Tooltip,
  ControlGroup,
} from "@blueprintjs/core";
import { IconNames } from "@blueprintjs/icons";
import { niceDateTime, apiCallPost, apiCallPatch } from "../../../../micro/fns/util";
import { inject, observer } from "mobx-react";
import { nanoid } from "nanoid";
import cogoToast from "cogo-toast";
import _ from "lodash";
import AsyncSelWrap from "../../../../micro/utils/AsyncSelWrap";
import SysInvCardV3 from "../../../components/systemic/SysInvCardV3";

class SystemicParamsTab extends React.Component {
  state = {
    searching: false,
    searchResults: [],
    isSavingSystemic: false,
    isSystemicDirty: false,
    originalSystemicParams: [], // Track original state for audit comparison
  };

  componentDidMount() {
    // Initialize original params for audit tracking
    this.setState({
      originalSystemicParams: JSON.parse(JSON.stringify(this.props.systemicInvestigations || [])),
    });
  }

  componentDidUpdate(prevProps) {
    // Update original params when props change (e.g., after successful save)
    if (prevProps.systemicInvestigations !== this.props.systemicInvestigations && !this.state.isSystemicDirty) {
      this.setState({
        originalSystemicParams: JSON.parse(JSON.stringify(this.props.systemicInvestigations || [])),
      });
    }
  }

  // Helper function to validate MongoDB ObjectID format
  isValidObjectId = (id) => {
    return id && /^[0-9a-fA-F]{24}$/.test(id);
  };

  // Create detailed audit logs for systemic parameter changes
  createDetailedAuditLogs = async () => {
    try {
      const auditEntries = [];
      const originalParams = this.state.originalSystemicParams || [];
      const currentParams = this.props.systemicInvestigations || [];

      // Track added parameters
      currentParams.forEach((currentParam) => {
        const originalParam = originalParams.find(
          (p) =>
            (p.nanoid && p.nanoid === currentParam.nanoid) ||
            (p._id_sr && p._id_sr === currentParam._id_sr) ||
            p.testName === currentParam.testName ||
            p.name === currentParam.name
        );

        if (!originalParam) {
          // New parameter added
          auditEntries.push({
            resourceType: "SURGERY_BOOKING",
            resourceId: this.props.surgery.id,
            resourceName: `Surgery - ${this.props.surgery.patientName || "Unknown Patient"}`,
            actionType: "UPDATE_SYSTEMIC_INVESTIGATIONS",
            actionSlug: `Added systemic parameter: "${currentParam.testName || currentParam.name}"`,
            actionMetadata: {
              fieldName: "systemicInvestigations",
              changeType: "ADD",
              previousValue: null,
              newValue: currentParam.testName || currentParam.name,
              paramDetails: {
                testName: currentParam.testName || currentParam.name,
                value: currentParam.value || "",
                unit: currentParam.unit || "",
                normalRange:
                  currentParam.unitNormLb && currentParam.unitNormUb
                    ? `${currentParam.unitNormLb}-${currentParam.unitNormUb}`
                    : "",
                status: currentParam.checked ? "Checked" : "Unchecked",
              },
            },
          });
        } else {
          // Check for value changes
          if ((originalParam.value || "") !== (currentParam.value || "")) {
            auditEntries.push({
              resourceType: "SURGERY_BOOKING",
              resourceId: this.props.surgery.id,
              resourceName: `Surgery - ${this.props.surgery.patientName || "Unknown Patient"}`,
              actionType: "UPDATE_SYSTEMIC_PARAM_VALUE",
              actionSlug: `Updated ${currentParam.testName || currentParam.name} value: "${
                originalParam.value || "Empty"
              }" → "${currentParam.value || "Empty"}"`,
              actionMetadata: {
                fieldName: "systemicInvestigations.value",
                changeType: "UPDATE",
                previousValue: originalParam.value || "",
                newValue: currentParam.value || "",
                paramDetails: {
                  testName: currentParam.testName || currentParam.name,
                  unit: currentParam.unit || "",
                },
              },
            });
          }

          // Check for unit changes
          if ((originalParam.unit || "") !== (currentParam.unit || "")) {
            auditEntries.push({
              resourceType: "SURGERY_BOOKING",
              resourceId: this.props.surgery.id,
              resourceName: `Surgery - ${this.props.surgery.patientName || "Unknown Patient"}`,
              actionType: "UPDATE_SYSTEMIC_PARAM_UNIT",
              actionSlug: `Updated ${currentParam.testName || currentParam.name} unit: "${
                originalParam.unit || "Empty"
              }" → "${currentParam.unit || "Empty"}"`,
              actionMetadata: {
                fieldName: "systemicInvestigations.unit",
                changeType: "UPDATE",
                previousValue: originalParam.unit || "",
                newValue: currentParam.unit || "",
                paramDetails: {
                  testName: currentParam.testName || currentParam.name,
                  value: currentParam.value || "",
                },
              },
            });
          }

          // Check for normal range changes
          const originalRange =
            originalParam.unitNormLb && originalParam.unitNormUb
              ? `${originalParam.unitNormLb}-${originalParam.unitNormUb}`
              : "";
          const currentRange =
            currentParam.unitNormLb && currentParam.unitNormUb
              ? `${currentParam.unitNormLb}-${currentParam.unitNormUb}`
              : "";

          if (originalRange !== currentRange) {
            auditEntries.push({
              resourceType: "SURGERY_BOOKING",
              resourceId: this.props.surgery.id,
              resourceName: `Surgery - ${this.props.surgery.patientName || "Unknown Patient"}`,
              actionType: "UPDATE_SYSTEMIC_PARAM_RANGE",
              actionSlug: `Updated ${currentParam.testName || currentParam.name} normal range: "${
                originalRange || "Empty"
              }" → "${currentRange || "Empty"}"`,
              actionMetadata: {
                fieldName: "systemicInvestigations.normalRange",
                changeType: "UPDATE",
                previousValue: originalRange,
                newValue: currentRange,
                paramDetails: {
                  testName: currentParam.testName || currentParam.name,
                  value: currentParam.value || "",
                  unit: currentParam.unit || "",
                },
              },
            });
          }

          // Check for status changes (checked/unchecked)
          if (Boolean(originalParam.checked) !== Boolean(currentParam.checked)) {
            const originalStatus = originalParam.checked ? "Checked" : "Unchecked";
            const currentStatus = currentParam.checked ? "Checked" : "Unchecked";

            auditEntries.push({
              resourceType: "SURGERY_BOOKING",
              resourceId: this.props.surgery.id,
              resourceName: `Surgery - ${this.props.surgery.patientName || "Unknown Patient"}`,
              actionType: "UPDATE_SYSTEMIC_PARAM_STATUS",
              actionSlug: `Updated ${
                currentParam.testName || currentParam.name
              } status: "${originalStatus}" → "${currentStatus}"`,
              actionMetadata: {
                fieldName: "systemicInvestigations.status",
                changeType: "UPDATE",
                previousValue: originalStatus,
                newValue: currentStatus,
                paramDetails: {
                  testName: currentParam.testName || currentParam.name,
                  value: currentParam.value || "",
                  unit: currentParam.unit || "",
                },
              },
            });
          }
        }
      });

      // Track removed parameters
      originalParams.forEach((originalParam) => {
        const stillExists = currentParams.find(
          (p) =>
            (p.nanoid && p.nanoid === originalParam.nanoid) ||
            (p._id_sr && p._id_sr === originalParam._id_sr) ||
            p.testName === originalParam.testName ||
            p.name === originalParam.name
        );

        if (!stillExists) {
          auditEntries.push({
            resourceType: "SURGERY_BOOKING",
            resourceId: this.props.surgery.id,
            resourceName: `Surgery - ${this.props.surgery.patientName || "Unknown Patient"}`,
            actionType: "UPDATE_SYSTEMIC_INVESTIGATIONS",
            actionSlug: `Removed systemic parameter: "${originalParam.testName || originalParam.name}"`,
            actionMetadata: {
              fieldName: "systemicInvestigations",
              changeType: "REMOVE",
              previousValue: originalParam.testName || originalParam.name,
              newValue: null,
              paramDetails: {
                testName: originalParam.testName || originalParam.name,
                value: originalParam.value || "",
                unit: originalParam.unit || "",
                normalRange:
                  originalParam.unitNormLb && originalParam.unitNormUb
                    ? `${originalParam.unitNormLb}-${originalParam.unitNormUb}`
                    : "",
                status: originalParam.checked ? "Checked" : "Unchecked",
              },
            },
          });
        }
      });

      // Send audit logs to backend if there are changes
      if (auditEntries.length > 0) {
        await apiCallPost(
          `/v3/${this.props.authStore.prefix}/neoSxAuditLog/createBulk`,
          auditEntries // Send array directly, not wrapped in object
        );

        console.log(`Created ${auditEntries.length} detailed audit log entries for systemic parameters:`, auditEntries);
      }
    } catch (err) {
      console.error("Failed to create detailed audit logs for systemic parameters:", err);
      // Don't throw error to avoid disrupting the save operation
    }
  };

  // Search for systemic parameters
  searchSystemicParams = _.debounce(async (string) => {
    if (!string) {
      this.setState({ searchResults: [] });
      return;
    }
    try {
      this.setState({ searching: true });
      const response = await apiCallPost(`/clinical/${this.props.authStore.prefix}/unisearch`, {
        prefix: "sysinv",
        query: string,
      });
      this.setState({ searchResults: response || [], searching: false });
    } catch (err) {
      cogoToast.error("Error searching systemic parameters. Please try again.");
      console.error("Search error:", err);
      this.setState({ searching: false });
    }
  }, 300);

  // Add systemic parameter
  addSystemicParam = async (item) => {
    const newParam = {
      ...item.data,
      testName: item.data.name || item.data.testName,
      modality: "Diagnostic",
      typeOfTest: "Quantitative",
      unit: item.data.unit || "",
      unitNormLb: item.data.unitNormLb || null,
      unitNormUb: item.data.unitNormUb || null,
      isPositiveGood: null,
      parentEntity: this.props.surgery.id,
      nanoid: nanoid(),
      _id_sr: item._id,
      type: "new",
      checked: false,
      value: "",
    };

    const updatedParams = [...(this.props.systemicInvestigations || []), newParam];
    this.props.onSystemicUpdate(updatedParams);
    this.setState({ isSystemicDirty: true });
  };

  // Save systemic parameters
  saveSystemicParams = async () => {
    try {
      this.setState({ isSavingSystemic: true });

      // Create detailed audit logs before saving
      await this.createDetailedAuditLogs();

      // Format data in V2 structure
      const payload = {
        systemicInvestigations: this.props.systemicInvestigations.map((param) => ({
          testName: param.name || param.testName,
          modality: "Diagnostic",
          typeOfTest: "Quantitative",
          unit: param.unit || "",
          unitNormLb: param.unitNormLb || null,
          unitNormUb: param.unitNormUb || null,
          isPositiveGood: null,
          parentEntity: this.props.surgery.id,
          nanoid: param.nanoid,
          _id_sr: param._id_sr,
          type: param.type || "new",
          checked: param.checked || false,
          value: param.value || "",
        })),
      };

      const response = await apiCallPatch(
        `/v3/${this.props.authStore.prefix}/neoSxBookingMaster/update/${this.props.surgery.id}`,
        payload
      );

      if (response.success) {
        cogoToast.success("Systemic parameters updated successfully");
        this.setState({
          isSystemicDirty: false,
          // Update original params to reflect saved changes for future audit comparisons
          originalSystemicParams: JSON.parse(JSON.stringify(this.props.systemicInvestigations || [])),
        });
      } else {
        throw new Error(response.message || "Failed to update systemic parameters");
      }
    } catch (err) {
      cogoToast.error("Failed to update systemic parameters");
      console.error("Error saving systemic parameters:", err);
    } finally {
      this.setState({ isSavingSystemic: false });
    }
  };

  // Update systemic parameter value
  updateSystemicParam = (index, value) => {
    const updatedParams = [...(this.props.systemicInvestigations || [])];
    updatedParams[index] = { ...updatedParams[index], value };
    this.props.onSystemicUpdate(updatedParams);
    this.setState({ isSystemicDirty: true });
  };

  // Remove systemic parameter
  removeSystemicParam = async (index) => {
    const updatedParams = [...(this.props.systemicInvestigations || [])];
    updatedParams.splice(index, 1);
    this.props.onSystemicUpdate(updatedParams);
    this.setState({ isSystemicDirty: true });
  };

  render() {
    const {
      surgery,
      systemicInvestigations,
      files,
      isLoadingFiles,
      isUploadingFile,
      uploadProgressDone,
      uploadProgressTotal,
      newFileBlob,
      previewFile,
      isPreviewOpen,
      onSystemicUpdate,
      onFileInputChange,
      onOpenPreview,
      onClosePreview,
      onDeleteFile,
    } = this.props;

    // Check if surgery ID is valid for file operations
    const hasSurgeryData = surgery && surgery.id;
    const hasValidSurgeryId = hasSurgeryData && this.isValidObjectId(surgery.id);

    return (
      <div style={{ padding: "20px" }}>
        <H3 style={{ marginBottom: "20px", color: "var(--text-primary)" }}>Systemic Parameters</H3>

        {/* Direct Systemic Parameters Section */}
        <Card
          elevation={Elevation.ONE}
          style={{
            marginBottom: "20px",
            padding: "20px",
            backgroundColor: "var(--bg-surface)",
          }}
        >
          <div style={{ marginBottom: "15px" }}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "15px",
              }}
            >
              <Tag large intent="primary">
                <Icon icon={IconNames.HEART} style={{ marginRight: "5px" }} />
                Systemic Parameters
              </Tag>
              {this.state.isSystemicDirty && (
                <Button
                  intent="primary"
                  icon="floppy-disk"
                  onClick={this.saveSystemicParams}
                  loading={this.state.isSavingSystemic}
                  disabled={this.state.isSavingSystemic}
                  small
                >
                  {this.state.isSavingSystemic ? "Saving..." : "Save"}
                </Button>
              )}
            </div>
            <ControlGroup>
              <AsyncSelWrap
                placeholder="Search and add systemic parameters..."
                searching={this.state.searching}
                items={this.state.searchResults}
                selectedItem={null}
                update={(item) => this.searchSystemicParams(item)}
                onChange={(item) => this.addSystemicParam(item)}
                style={{ flex: 1, minWidth: "300px" }}
              />
            </ControlGroup>
          </div>

          {/* Display Added Systemic Parameters */}
          <div style={{ display: "flex", flexDirection: "column" }}>
            {!(systemicInvestigations || []).length ? (
              <div style={{ marginTop: "10px" }}>
                <Tag minimal large style={{ color: "var(--text-secondary)" }}>
                  <Icon icon={IconNames.INFO_SIGN} style={{ marginRight: "5px" }} />
                  No systemic parameters added yet. Use the search above to add parameters.
                </Tag>
              </div>
            ) : (
              (systemicInvestigations || []).map((param, index) => (
                <SysInvCardV3
                  key={param.nanoid || param._id || index}
                  data={param}
                  update={(value) => this.updateSystemicParam(index, value)}
                  remove={() => this.removeSystemicParam(index)}
                  horizontal={false}
                />
              ))
            )}
          </div>
        </Card>

        {/* Enhanced Files Uploader and List */}
        <Card elevation={Elevation.ONE} style={{ marginTop: "20px", padding: "20px" }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              marginBottom: "15px",
            }}
          >
            <H4 style={{ margin: 0, color: "var(--text-primary)" }}>
              <Icon icon={IconNames.PAPERCLIP} style={{ marginRight: "8px" }} />
              Document Attachments
            </H4>
            <Tag minimal intent="primary">
              {(files || []).length} file{(files || []).length !== 1 ? "s" : ""}
            </Tag>
          </div>

          {/* Surgery ID Validation Warning */}
          {!hasValidSurgeryId && (
            <Callout intent={Intent.WARNING} icon={IconNames.WARNING_SIGN} style={{ marginBottom: "15px" }}>
              <Text style={{ fontSize: "14px" }}>
                <strong>File upload temporarily unavailable:</strong> Invalid surgery ID format detected.
                {hasSurgeryData
                  ? ` Current ID: ${surgery.id}. Please contact support if this issue persists.`
                  : " Surgery data not loaded. Please refresh the page."}
              </Text>
            </Callout>
          )}

          {/* File Upload Section */}
          <Card
            elevation={Elevation.ZERO}
            style={{
              padding: "15px",
              backgroundColor: "var(--bg-surface)",
              border: "2px dashed var(--border-color)",
              borderRadius: "8px",
            }}
          >
            <div
              style={{
                display: "flex",
                gap: "12px",
                alignItems: "center",
                flexWrap: "wrap",
              }}
            >
              <Tag minimal large intent="primary">
                <Icon icon={IconNames.UPLOAD} style={{ marginRight: "5px" }} />
                Upload File
              </Tag>
              <FileInput
                text={newFileBlob ? newFileBlob.name : "Choose file..."}
                buttonText="Browse"
                disabled={isUploadingFile || !hasValidSurgeryId}
                onInputChange={hasValidSurgeryId ? onFileInputChange : null}
                style={{ flex: 1, minWidth: "200px" }}
              />
              {newFileBlob && !isUploadingFile && (
                <Tooltip content="Clear selected file">
                  <Button
                    small
                    minimal
                    icon={IconNames.CROSS}
                    onClick={() => onFileInputChange({ target: { files: [] } })}
                  />
                </Tooltip>
              )}
            </div>

            {/* Upload Progress */}
            {isUploadingFile && (
              <div style={{ marginTop: "12px" }}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: "5px",
                  }}
                >
                  <Text style={{ fontSize: "12px", color: "var(--text-secondary)" }}>
                    Uploading {newFileBlob?.name}...
                  </Text>
                  <Text style={{ fontSize: "12px", color: "var(--text-secondary)" }}>
                    {((uploadProgressDone / uploadProgressTotal) * 100).toFixed(0)}%
                  </Text>
                </div>
                <ProgressBar
                  value={uploadProgressDone / uploadProgressTotal}
                  intent={Intent.PRIMARY}
                  stripes={true}
                  animate={true}
                />
              </div>
            )}

            {/* File Upload Guidelines */}
            <Callout intent={Intent.NONE} style={{ marginTop: "12px", padding: "8px 12px" }} icon={IconNames.INFO_SIGN}>
              <Text style={{ fontSize: "12px", color: "var(--text-secondary)" }}>
                Supported formats: PDF, DOC, DOCX, JPG, PNG, TXT (Max size: 10MB)
              </Text>
            </Callout>
          </Card>

          {/* File List Section */}
          <div style={{ marginTop: "20px" }}>
            {isLoadingFiles ? (
              <NonIdealState
                icon={<Spinner />}
                title="Loading documents..."
                description="Please wait while we fetch your uploaded files"
              />
            ) : (
              <div style={{ display: "grid", gap: "12px" }}>
                {!(files || []).length ? (
                  <NonIdealState
                    icon={IconNames.DOCUMENT}
                    title="No documents uploaded"
                    description="Upload your first document using the form above"
                    action={
                      <Text style={{ color: "var(--text-secondary)", fontSize: "14px" }}>
                        Documents will appear here once uploaded
                      </Text>
                    }
                  />
                ) : (
                  files.map((file, index) => {
                    const fileExtension = file.identifier?.split(".").pop()?.toLowerCase() || "";
                    const getFileIcon = () => {
                      switch (fileExtension) {
                        case "pdf":
                          return IconNames.DOCUMENT;
                        case "doc":
                        case "docx":
                          return IconNames.ALIGN_LEFT;
                        case "jpg":
                        case "jpeg":
                        case "png":
                        case "gif":
                          return IconNames.MEDIA;
                        case "txt":
                          return IconNames.DOCUMENT;
                        default:
                          return IconNames.PAPERCLIP;
                      }
                    };

                    const getFileSize = () => {
                      if (file.size) {
                        const kb = file.size / 1024;
                        if (kb < 1024) return `${kb.toFixed(1)} KB`;
                        const mb = kb / 1024;
                        return `${mb.toFixed(1)} MB`;
                      }
                      return "Unknown size";
                    };

                    return (
                      <Card
                        key={file._id}
                        elevation={Elevation.ONE}
                        style={{
                          padding: "15px",
                          backgroundColor: "var(--bg-surface)",
                          border: "1px solid var(--border-color)",
                          transition: "all 0.2s ease",
                        }}
                        className="file-item-hover"
                      >
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "flex-start",
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              gap: "12px",
                              alignItems: "flex-start",
                              flex: 1,
                            }}
                          >
                            <Icon
                              icon={getFileIcon()}
                              size={24}
                              style={{
                                color: "var(--text-primary)",
                                marginTop: "2px",
                                minWidth: "24px",
                              }}
                            />
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <Text
                                style={{
                                  fontWeight: 600,
                                  color: "var(--text-primary)",
                                  fontSize: "14px",
                                  display: "block",
                                  marginBottom: "8px",
                                  wordBreak: "break-word",
                                }}
                              >
                                {file.entityName || file.identifier}
                              </Text>
                              <div
                                style={{
                                  display: "flex",
                                  gap: "8px",
                                  alignItems: "center",
                                  flexWrap: "wrap",
                                  marginBottom: "8px",
                                }}
                              >
                                <Tag minimal small intent="success" icon={IconNames.USER}>
                                  {file.createdByName || "Unknown User"}
                                </Tag>
                                <Tag minimal small intent="primary" icon={IconNames.TIME}>
                                  {niceDateTime(file.createdAt)}
                                </Tag>
                                {file.size && (
                                  <Tag minimal small icon={IconNames.DATABASE}>
                                    {getFileSize()}
                                  </Tag>
                                )}
                              </div>
                            </div>
                          </div>

                          <div style={{ display: "flex", gap: "6px", marginLeft: "12px" }}>
                            <Tooltip content="Preview document">
                              <Button
                                small
                                minimal
                                icon={IconNames.EYE_OPEN}
                                onClick={() => onOpenPreview(file)}
                                intent={Intent.PRIMARY}
                              />
                            </Tooltip>
                            <Tooltip content="Download document">
                              <Button
                                small
                                minimal
                                icon={IconNames.DOWNLOAD}
                                onClick={() => window.open(file.directLink, "_blank")}
                                intent={Intent.SUCCESS}
                              />
                            </Tooltip>
                            <Tooltip content="Delete document">
                              <Button
                                small
                                minimal
                                icon={IconNames.TRASH}
                                intent={Intent.DANGER}
                                onClick={() => onDeleteFile(file)}
                              />
                            </Tooltip>
                          </div>
                        </div>
                      </Card>
                    );
                  })
                )}
              </div>
            )}
          </div>

          {/* Enhanced Preview Dialog */}
          <Dialog
            isOpen={isPreviewOpen}
            onClose={onClosePreview}
            title={
              <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                <Icon icon={IconNames.EYE_OPEN} />
                <Text style={{ fontWeight: 600 }}>
                  {previewFile?.entityName || previewFile?.identifier || "Document Preview"}
                </Text>
              </div>
            }
            style={{
              backgroundColor: "var(--bg-primary)",
              width: "95vw",
              maxWidth: "1400px",
              height: "95vh",
            }}
            canOutsideClickClose={true}
            canEscapeKeyClose={true}
          >
            <div className={DialogClasses.DIALOG_BODY} style={{ padding: "0", height: "calc(95vh - 120px)" }}>
              {previewFile ? (
                <div style={{ height: "100%", display: "flex", flexDirection: "column" }}>
                  {/* File Info Bar */}
                  <div
                    style={{
                      padding: "12px 20px",
                      backgroundColor: "var(--bg-surface)",
                      borderBottom: "1px solid var(--border-color)",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
                      <Tag minimal intent="primary" icon={IconNames.USER}>
                        {previewFile.createdByName || "Unknown User"}
                      </Tag>
                      <Tag minimal intent="success" icon={IconNames.TIME}>
                        {niceDateTime(previewFile.createdAt)}
                      </Tag>
                      {previewFile.size && (
                        <Tag minimal icon={IconNames.DATABASE}>
                          {(() => {
                            const kb = previewFile.size / 1024;
                            if (kb < 1024) return `${kb.toFixed(1)} KB`;
                            const mb = kb / 1024;
                            return `${mb.toFixed(1)} MB`;
                          })()}
                        </Tag>
                      )}
                    </div>
                    <div style={{ display: "flex", gap: "8px" }}>
                      <Button
                        small
                        icon={IconNames.DOWNLOAD}
                        onClick={() => window.open(previewFile.directLink, "_blank")}
                        intent={Intent.SUCCESS}
                      >
                        Download
                      </Button>
                      <Button
                        small
                        icon={IconNames.SHARE}
                        onClick={() => window.open(previewFile.directLink, "_blank")}
                      >
                        Open in New Tab
                      </Button>
                    </div>
                  </div>

                  {/* Preview Content */}
                  <div style={{ flex: 1, position: "relative" }}>
                    <iframe
                      title="Document Preview"
                      src={previewFile.directLink}
                      style={{
                        width: "100%",
                        height: "100%",
                        border: 0,
                        backgroundColor: "white",
                      }}
                      onLoad={() => console.log("Document loaded successfully")}
                      onError={() => console.error("Failed to load document")}
                    />
                  </div>
                </div>
              ) : (
                <NonIdealState
                  icon={IconNames.ERROR}
                  title="Preview not available"
                  description="Unable to load document preview"
                />
              )}
            </div>
            <div className={DialogClasses.DIALOG_FOOTER}>
              <div className={DialogClasses.DIALOG_FOOTER_ACTIONS}>
                <Button onClick={onClosePreview} intent={Intent.PRIMARY} large>
                  <Icon icon={IconNames.CROSS} style={{ marginRight: "5px" }} />
                  Close Preview
                </Button>
              </div>
            </div>
          </Dialog>
        </Card>
      </div>
    );
  }
}

export default inject("authStore")(observer(SystemicParamsTab));
